<template>
  <div class="ml-4">
    <!-- New 3-state logic when feature flag is enabled -->
    <template v-if="isEnhancedStatesEnabled">
      <CButton v-if="isExecutedStatus" circle size="medium" type="success" @click.stop="showModal = true">
        <template #icon>
          <CIcon class="flex items-center justify-center" icon="check" size="16px" />
        </template>
      </CButton>

      <CButton v-else-if="isNotExecutedStatus" circle size="medium" type="error" @click.stop="showModal = true">
        <template #icon>
          <CIcon class="flex items-center justify-center" icon="close" size="16px" />
        </template>
      </CButton>

      <CButton v-else-if="isMixedStatus" circle size="medium" type="warning" @click.stop="showModal = true">
        <template #icon>
          <CIcon class="flex items-center justify-center" icon="exclamation" size="16px" />
        </template>
      </CButton>

      <CButton v-else circle size="medium" @click.stop="showModal = true">
        <template #icon>
          <CIcon class="flex items-center justify-center" icon="square" size="16px" />
        </template>
      </CButton>
    </template>

    <!-- Legacy 2-state logic when feature flag is disabled -->
    <template v-else>
      <CButton v-if="executedLegacy" circle size="medium" type="success" @click.stop="showModal = true">
        <template #icon>
          <CIcon class="flex items-center justify-center" icon="check" size="16px" />
        </template>
      </CButton>
      <CButton v-else circle size="medium" @click.stop="showModal = true">
        <template #icon>
          <CIcon class="flex items-center justify-center" icon="square" size="16px" />
        </template>
      </CButton>
    </template>

    <CModal v-model:show="showModal" :mask-closable="!isEnhancedStatesEnabled" :style="{ width: iframeWidth }">
      <CCard class="pa-0">
        <iframe
          ref="embeddableIframe"
          class="w-full border-none"
          :data-app="executionApp.app_metadata?.name"
          :height="iframeHeight"
          :src="executionAppUrl"
          width="100%"
          @load="updateIframeData"
        />
      </CCard>
    </CModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, inject, type ComputedRef } from 'vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CCard from '@lib/components/CCard/CCard.vue'
import CModal from '@lib/components/CModal/CModal.vue'
import type { CanvasRetrieve, CanvasSection } from '@openapi'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { CLIENT_TYPE, PLATFORM_TYPE } from '@lib/constants/cdp.const'
import type { AppFile } from '@lib/types/app'
import { doesLocalOverrideExist, getLocalOverrideUrl } from '@lib/util/dev.util'
import { deepToRaw } from '@lib/util/reactive'
import type { UiAppSetDataPayload } from '@sdk/api/modules/ui/types.ui'
import { UI_MESSAGE_TYPES, UI_API_METHOD_TYPES } from '@sdk/api/modules/ui/types.ui'
import { CanvasBuilderMode } from '@canvas-builder/types/canvas'
import { isOriginValid } from '@lib/util/message-event'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const { getEnv, activeCanvas, isEditMode, mode, inCall, displayMode } = useCanvas()

// LaunchDarkly integration
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const isEnhancedStatesEnabled = computed(() => launchDarkly.value?.enable_enhanced_section_execution_states ?? false)

const props = defineProps<{
  section: CanvasSection
  executionApp: AppFile
}>()

const sectionListSectionInfo = inject<
  ComputedRef<{
    usedInSectionId: string
    sectionIdx: number
    sectionListId: string
  } | null>
>(
  'sectionListSectionInfo',
  computed(() => null),
)
const executionAppUrl = computed(() => {
  if (!props.executionApp?.content_url) return undefined
  const name = props.executionApp.app_metadata?.name
  if (doesLocalOverrideExist(name)) {
    return getLocalOverrideUrl(name)
  }
  return props.executionApp.content_url
})

// Enhanced execution state logic (new 3-state behavior)
const sectionExecution = computed(() => activeCanvas.value?.context?.sectionExecutions?.[props.section.id])
const isExecuted = computed(() => sectionExecution.value?.executed)
const isExpiredValue = computed(() => sectionExecution.value?.isExpired)
const executionStatus = computed(() => sectionExecution.value?.executionStatus)

const isExecutedStatus = computed(
  () => isExpiredValue.value !== true && isExecuted.value === true && executionStatus.value === 'executed',
)

const isNotExecutedStatus = computed(
  () => isExpiredValue.value !== true && isExecuted.value === true && executionStatus.value === 'not_executed',
)

const isMixedStatus = computed(
  () => isExpiredValue.value !== true && isExecuted.value === true && executionStatus.value === 'mixed',
)

// Legacy execution state logic (old 2-state behavior)
const executedLegacy = computed(() => Boolean(activeCanvas.value?.context?.sectionExecutions?.[props.section.id]))

const showModal = ref<boolean>(false)
const embeddableIframe = ref<HTMLIFrameElement | null>(null)
const iframeWidth = computed(
  () => props.executionApp?.app_metadata?.module?.canvas_section_execution?.defaults?.dimensions?.width || '500px',
)
const iframeHeight = ref(
  props.executionApp?.app_metadata?.module?.canvas_section_execution?.defaults?.dimensions?.height || '300px',
)

const client = computed(() =>
  mode.value
    ? {
        [CanvasBuilderMode.ADMIN]: CLIENT_TYPE.admin,
        [CanvasBuilderMode.IMPACT]: CLIENT_TYPE.impact,
        [CanvasBuilderMode.SHARING]: CLIENT_TYPE.dsr,
        [CanvasBuilderMode.HOME]: CLIENT_TYPE.impactCanvasHome,
      }[mode.value]
    : null,
)

function handleAppMethod(event: MessageEvent<any>) {
  if (!isOriginValid(event)) return
  if (event.source !== embeddableIframe.value?.contentWindow) return

  const { type, body } = event.data

  if (type === UI_API_METHOD_TYPES.UI_CLOSE_CANVAS_SECTION_EXECUTION) {
    showModal.value = false
  } else if (type === UI_API_METHOD_TYPES.UI_APP_RESIZE) {
    iframeHeight.value = `${String(body?.height)}px`
  } else if (type === UI_API_METHOD_TYPES.UI_APP_LOADED) {
    updateIframeData()
  }
}

const updateIframeData = async () => {
  const envData = client.value === 'impact' ? await getEnv?.value?.() : { mode: 'WEB', pitcher: { user: null } }
  const user = envData?.pitcher?.user ? deepToRaw(envData.pitcher.user) : null
  const canvasWithoutSectionsContent = deepToRaw({
    ...activeCanvas.value,
    sections: activeCanvas.value?.sections.map(({ content, ...s }) => s) ?? [],
  })

  const setDataPayload: UiAppSetDataPayload = {
    client: client.value,
    platform: envData
      ? {
          IOS: PLATFORM_TYPE.ios,
          WEB: PLATFORM_TYPE.web,
          ANDROID: PLATFORM_TYPE.android,
          WINDOWS: PLATFORM_TYPE.windows,
        }[envData.mode]
      : undefined,
    user,
    is_edit_mode: isEditMode.value ?? false,
    in_call: inCall.value ?? false,
    canvas: canvasWithoutSectionsContent as CanvasRetrieve,
    used_in_section: props.section,
    display_mode: displayMode.value,
    full_screen_mode: false,
    height: iframeHeight.value,
    width: iframeWidth.value,
    settings: null,
    component_id: props.section.content?.data?.[0]?.id,
    section_index: sectionListSectionInfo.value?.sectionIdx ?? null,
    section_list_id: sectionListSectionInfo.value?.sectionListId ?? null,
    canvas_correlation_id: envData && 'state' in envData ? envData.state?.canvas_correlation_id : undefined,
  }

  embeddableIframe.value?.contentWindow?.postMessage(
    {
      type: UI_MESSAGE_TYPES.UI_APP_SET_DATA,
      body: setDataPayload,
    },
    '*',
  )
}

function cleanupIframe() {
  if (embeddableIframe.value) {
    embeddableIframe.value.src = 'about:blank'
    embeddableIframe.value = null
  }
}

watch(showModal, (visible: boolean) => {
  if (visible) {
    window.addEventListener('message', handleAppMethod)
  } else {
    window.removeEventListener('message', handleAppMethod)
    cleanupIframe()
  }
  showModal.value = visible
})

onBeforeUnmount(() => {
  window.removeEventListener('message', handleAppMethod)
  cleanupIframe()
})
</script>
